import { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Button, Table, Form } from "react-bootstrap";
import Reveal from "../Reveal/Reveal";
import Lightbox from "../N1/Lightbox";
import WaitingListForm from "../WaitingList/WaitingListForm";
import { N1_IMAGES, N1_STUDIO, N1_LIFESTYLE } from "../N1/images";
import {
  N1_VARIANTS,
  N1_CONFIGURATIONS,
  N1_MOUNTING,
  formatEuro,
  n1Variant,
} from "../../config/n1";
import "../N1/N1.css";

const TITLE = "N1 — Installation loudspeaker for venues & cafés | SonicBeats";
const META =
  'The N1 is a two-way installation loudspeaker built for cafés, bars, restaurants, venues and cinema halls. Available in 8", 12" and 15" versions, passive or active, in the colour of your choice.';

/* Only the rows that differ by size; what all three share is one line below
   the table. */
const SPEC_ROWS = [
  ["Woofer", (v) => v.woofer.replace('"', " inch")],
  ["Power (RMS)", (v) => `${v.rms} W`],
  ["Typical use", (v) => v.typicalUse],
  ["Active", (v) => formatEuro(v.price.active)],
  ["Passive", (v) => formatEuro(v.price.passive)],
];

/* Title, meta description and schema.org Product data for this page only —
   put back on the way out so the rest of the site keeps its own. */
function usePageMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousMeta = meta && meta.getAttribute("content");

    document.title = TITLE;
    if (meta) meta.setAttribute("content", META);

    const origin = window.location.origin;
    const structured = document.createElement("script");
    structured.type = "application/ld+json";
    structured.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: "SonicBeats N1",
      description: META,
      brand: { "@type": "Brand", name: "SonicBeats" },
      image: N1_IMAGES.map((img) => origin + img.src),
      offers: N1_VARIANTS.flatMap((variant) =>
        N1_CONFIGURATIONS.map((config) => ({
          "@type": "Offer",
          name: `${variant.id} ${config.label}`,
          sku: `${variant.id}-${config.id}`,
          priceCurrency: "EUR",
          price: variant.price[config.id],
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: variant.price[config.id],
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
          },
          availability: "https://schema.org/PreOrder",
          url: origin + window.location.pathname,
        })),
      ),
    });
    document.head.appendChild(structured);

    return () => {
      document.title = previousTitle;
      if (meta && previousMeta !== null)
        meta.setAttribute("content", previousMeta);
      structured.remove();
    };
  }, []);
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="mb-3">
      <div className="n1-control-label" id={`n1-${label}`}>
        {label}
      </div>
      <div
        className="n1-segmented"
        role="group"
        aria-labelledby={`n1-${label}`}
      >
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`n1-segment ${value === option.id ? "is-selected" : ""}`}
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
          >
            {option.label}
            {option.hint && (
              <span className="n1-segment-hint">{option.hint}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function N1() {
  const [variantId, setVariantId] = useState(N1_VARIANTS[0].id);
  const [configId, setConfigId] = useState(N1_CONFIGURATIONS[0].id);
  const [mainIndex, setMainIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  usePageMeta();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const variant = n1Variant(variantId);
  const config = N1_CONFIGURATIONS.find((c) => c.id === configId);
  const main = N1_STUDIO[mainIndex];

  const scrollToForm = () =>
    document
      .getElementById("waiting-list")
      .scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      {/* 1–4 · Product, selector, call to action */}
      <Container className="mt-5 pt-5">
        <Row className="g-4 g-lg-5 mt-2 align-items-start">
          <Col xs={12} lg={7}>
            <button
              type="button"
              className="n1-stage rounded-4"
              onClick={() => setLightbox(mainIndex)}
              aria-label={`Enlarge: ${main.caption}`}
            >
              <img src={main.src} alt={main.alt} />
            </button>
            <div className="d-flex gap-2 mt-3">
              {N1_STUDIO.map((img, index) => (
                <button
                  type="button"
                  key={img.src}
                  className={`n1-thumb ${index === mainIndex ? "is-selected" : ""}`}
                  onClick={() => setMainIndex(index)}
                  aria-label={`Show ${img.caption.toLowerCase()}`}
                  aria-pressed={index === mainIndex}
                >
                  <img src={img.src} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          </Col>

          <Col xs={12} lg={5} className="n1-buy">
            <span className="sb-eyebrow">Installation loudspeaker</span>
            <h1 className="display-3 fw-bold mb-3">N1</h1>
            <p className="fs-5 fw-light mb-4">
              Architectural sound for venues that are looked at as much as they
              are listened to.
            </p>

            <Segmented
              label="Size"
              value={variantId}
              onChange={setVariantId}
              options={N1_VARIANTS.map((v) => ({
                id: v.id,
                label: v.id,
                hint: v.woofer,
              }))}
            />
            <Segmented
              label="Configuration"
              value={configId}
              onChange={setConfigId}
              options={N1_CONFIGURATIONS}
            />
            <div className="n1-price-row mt-4 mb-4">
              <div>
                <div className="n1-price" aria-live="polite">
                  {formatEuro(variant.price[configId])}
                </div>
                <div className="small text-muted">
                  Per speaker · Prices exclude VAT
                </div>
              </div>
              <div className="text-end">
                <div className="fw-bold">{variant.rms} W RMS</div>
                <div className="small text-muted">{variant.woofer} woofer</div>
              </div>
            </div>

            <div className="d-grid">
              <Button
                variant="primary"
                className="btn-rounded py-2"
                onClick={scrollToForm}
              >
                Join the waiting list
              </Button>
            </div>
            <p className="small text-muted mt-3 mb-0">
              Built to order — we will contact you with a lead time and a quote.
            </p>
          </Col>
        </Row>
      </Container>

      {/* Design + specifications */}
      <Container className="py-section">
        <Row className="g-4 g-lg-5 align-items-start">
          <Col xs={12} lg={5}>
            <Reveal>
              <span className="sb-eyebrow">The design</span>
              <h2 className="display-6 fw-bold mb-3">Designed to be seen.</h2>
              <p className="text-muted">
                The sculpted front grille is a structural element, not a cover:
                an open lattice that lets the driver and the birch cabinet show
                through. The side panels carry a faceted diffuser pattern in
                matching colours.
              </p>
              <p className="text-muted mb-0">
                Behind it is a proper two-way system — even coverage at
                background-music levels, and real headroom when the room fills
                up.
              </p>
            </Reveal>
          </Col>
          <Col xs={12} lg={7}>
            <Reveal delay={0.08}>
              <div className="table-responsive">
                <Table className="n1-specs mb-2">
                  <thead>
                    <tr>
                      <th scope="col">
                        <span className="visually-hidden">Specification</span>
                      </th>
                      {N1_VARIANTS.map((v) => (
                        <th
                          scope="col"
                          key={v.id}
                          className={v.id === variantId ? "is-selected" : ""}
                        >
                          {v.id}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {SPEC_ROWS.map(([label, value]) => (
                      <tr key={label}>
                        <th scope="row">{label}</th>
                        {N1_VARIANTS.map((v) => (
                          <td
                            key={v.id}
                            className={v.id === variantId ? "is-selected" : ""}
                          >
                            {value(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <p className="small text-muted mb-0">
                All sizes: 2-way, ported front-firing, birch plywood cabinet.
                Prices per speaker, excl. VAT.
              </p>
            </Reveal>
          </Col>
        </Row>
      </Container>

      {/* Gallery */}
      <Container className="pb-section">
        <span className="sb-eyebrow">In the room</span>
        <div className="n1-gallery">
          {N1_LIFESTYLE.map((img, index) => (
            <button
              type="button"
              key={img.src}
              className="n1-gallery-item rounded-4"
              onClick={() => setLightbox(N1_STUDIO.length + index)}
              aria-label={`Enlarge: ${img.caption}`}
            >
              <img src={img.src} alt={img.alt} loading="lazy" />
            </button>
          ))}
        </div>
      </Container>

      {/* Mounting, finishes & installation */}
      <Container className="pb-section">
        <Reveal>
          <div className="bg-custom-color-grey rounded-4 p-4 p-md-5">
            <Row className="g-4">
              <Col xs={12} md={4}>
                <span className="sb-eyebrow mb-2">Mounting</span>
                <p className="text-muted small mb-0">
                  On chains from the fly points in the top panel, on a floor or
                  table stand, on a wall or ceiling bracket — or a custom mount
                  made for the room.
                </p>
              </Col>
              <Col xs={12} md={4}>
                <span className="sb-eyebrow mb-2">Finishes</span>
                <p className="text-muted small mb-0">
                  Grille and side panels in a range of colours, specified
                  separately. Birch plywood cabinet as standard.
                </p>
              </Col>
              <Col xs={12} md={4}>
                <span className="sb-eyebrow mb-2">Installation</span>
                <p className="text-muted small mb-0">
                  We can install the complete system, or recommend an installer
                  where the location is not accessible to us. Quoted per
                  project.
                </p>
              </Col>
            </Row>
          </div>
        </Reveal>
      </Container>

      {/* Waiting list */}
      <Container className="pb-section" id="waiting-list">
        <Row className="g-4 g-lg-5">
          <Col xs={12} lg={4}>
            <span className="sb-eyebrow">Waiting list</span>
            <h2 className="display-6 fw-bold">Join the waiting list.</h2>
            <p className="text-muted">
              Not yet in stock. Built to order — we will contact you with a lead
              time and a quote.
            </p>
          </Col>
          <Col xs={12} lg={8}>
            <WaitingListForm
              product={`${variant.id} ${config.label}`}
              details={[
                `Indicative price: ${formatEuro(variant.price[configId])} per speaker, excl. VAT`,
              ]}
              venue
              mountingOptions={N1_MOUNTING}
            >
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="waitlist-model">
                  <Form.Label>Model</Form.Label>
                  <Form.Select
                    value={variantId}
                    onChange={(e) => setVariantId(e.target.value)}
                  >
                    {N1_VARIANTS.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.id} ({v.woofer})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="waitlist-configuration">
                  <Form.Label>Configuration</Form.Label>
                  <Form.Select
                    value={configId}
                    onChange={(e) => setConfigId(e.target.value)}
                  >
                    {N1_CONFIGURATIONS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </WaitingListForm>
          </Col>
        </Row>
      </Container>

      <Lightbox
        images={N1_IMAGES}
        index={lightbox}
        onClose={closeLightbox}
        onIndexChange={setLightbox}
      />
    </>
  );
}

export default N1;
